import type { TicketStatusChoice } from "@/modules/tickets/ticketInboxConfig";

export type TicketsOverviewView = "table" | "kanban" | "types";

export const TICKETS_OVERVIEW_VIEW_KEY = "nomi.tickets.overviewView";

export const TICKET_KANBAN_COLUMNS = [
  { id: "new", label: "New", color: "#3b82f6" },
  { id: "open", label: "Open", color: "#0ea5e9" },
  { id: "waiting", label: "Waiting", color: "#f59e0b" },
  { id: "resolved", label: "Resolved", color: "#94a3b8" },
] as const;

export type TicketKanbanColumn = {
  id: string;
  label: string;
  color: string;
};

/** @deprecated Prefer string column ids from `ticketKanbanColumns`. */
export type TicketKanbanColumnId = (typeof TICKET_KANBAN_COLUMNS)[number]["id"];

const BUILTIN_KANBAN_IDS = new Set<string>(
  TICKET_KANBAN_COLUMNS.map((column) => column.id),
);

const CUSTOM_KANBAN_COLOR = "#94a3b8";

/** Builtins + active custom status choices as board columns. */
export const ticketKanbanColumns = (
  choices: TicketStatusChoice[] = [],
): TicketKanbanColumn[] => {
  const builtins: TicketKanbanColumn[] = TICKET_KANBAN_COLUMNS.map(
    (column) => ({
      id: column.id,
      label: column.label,
      color: column.color,
    }),
  );
  const extras: TicketKanbanColumn[] = [];
  for (const choice of choices) {
    const id = choice.id?.trim();
    if (!id || BUILTIN_KANBAN_IDS.has(id)) continue;
    if (extras.some((column) => column.id === id)) continue;
    extras.push({
      id,
      label: choice.name?.trim() || id,
      color: CUSTOM_KANBAN_COLOR,
    });
  }
  return [...builtins, ...extras];
};

export const isTicketKanbanColumnId = (
  value: string | null | undefined,
  columnIds?: readonly string[],
): boolean => {
  if (!value) return false;
  if (columnIds) return columnIds.includes(value);
  return BUILTIN_KANBAN_IDS.has(value);
};

export const readPersistedTicketsOverviewView = (): TicketsOverviewView => {
  if (typeof window === "undefined") return "table";
  const stored = window.localStorage.getItem(TICKETS_OVERVIEW_VIEW_KEY);
  if (stored === "kanban" || stored === "types") return stored;
  return "table";
};

export const ticketStatusForKanban = (
  status?: string | null,
  columnIds?: readonly string[],
): string => {
  const ids = columnIds ?? [...BUILTIN_KANBAN_IDS];
  if (status && ids.includes(status)) return status;
  return "open";
};

export const emptyTicketKanbanBuckets = <T>(
  columnIds?: readonly string[],
): Record<string, T[]> => {
  const ids = columnIds ?? TICKET_KANBAN_COLUMNS.map((column) => column.id);
  return Object.fromEntries(ids.map((id) => [id, [] as T[]])) as Record<
    string,
    T[]
  >;
};

/** Display-only subject cleanup for board/table cards. */
export const formatTicketCardSubject = (subject?: string | null) => {
  const trimmed = subject?.trim() || "";
  if (!trimmed) return "No subject";
  return trimmed.replace(/^(?:(?:re|fw|fwd)\s*:\s*)+/i, "").trim() || trimmed;
};

export type TicketCardRailTone = "unpaid" | "unread" | "priority" | "default";

export const resolveTicketCardRailTone = (params: {
  unpaid: boolean;
  unread: boolean;
  elevatedPriority: boolean;
}): TicketCardRailTone => {
  if (params.unpaid) return "unpaid";
  if (params.unread) return "unread";
  if (params.elevatedPriority) return "priority";
  return "default";
};
